import { parse } from 'papaparse'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { commercialApi } from '@/interfaces/spark-api'
import type { components } from '#/types/capi-generated'

type GasTransitUnit = components['schemas']['CAPIGasTransitCostsUnitEnum']
type GasTransitType = components['schemas']['CAPIGasTransitTypeEnum']
type GasTransitProduct = components['schemas']['CAPIGasTransitProductEnum']

export interface GasTransitRoute {
  origin: string
  destination: string
}

export interface GasTransitCostsOpts {
  origin?: string
  destination?: string
  type?: GasTransitType
  product?: GasTransitProduct
  start?: string
  end?: string
  limit?: number
  offset?: number
}

export const useGasTransitStore = defineStore('gas-transit', () => {
  const hasGasTransitPermission = ref(false)
  const routes = ref<GasTransitRoute[]>([])

  const origins = computed(() => {
    const seen = new Set<string>()
    for (const r of routes.value) seen.add(r.origin)
    return Array.from(seen).sort((a, b) => a.localeCompare(b))
  })

  const destinations = computed(() => {
    const seen = new Set<string>()
    for (const r of routes.value) seen.add(r.destination)
    return Array.from(seen).sort((a, b) => a.localeCompare(b))
  })

  function destinationsFor(origin: string): string[] {
    const seen = new Set<string>()
    for (const r of routes.value) {
      if (r.origin === origin) seen.add(r.destination)
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b))
  }

  function parseCsv(csv: string): { columns: string[]; rows: string[][] } {
    const { data, meta, errors } = parse<Record<string, string>>(csv, {
      header: true,
      delimiter: ',',
      skipEmptyLines: true,
    })

    if (errors.length > 0) {
      console.error('CSV parse errors:', errors)
    }

    const columns = meta.fields ?? []
    const rows = data
      .map((item) => Object.values(item))
      .filter((item) => item.length === columns.length)

    return { columns, rows }
  }

  async function fetchReferenceData(): Promise<GasTransitRoute[]> {
    try {
      const response = await commercialApi.fetchGasTransitReferenceData()
      const data = response?.data ?? []
      routes.value = data.map((r) => ({
        origin: r.origin,
        destination: r.destination,
      }))
      hasGasTransitPermission.value = true
      return routes.value
    } catch (err) {
      hasGasTransitPermission.value = false
      console.warn('fetchGasTransitReferenceData failed', err)
      return []
    }
  }

  async function fetchCosts(
    unit: GasTransitUnit,
    opts: GasTransitCostsOpts = {},
  ): Promise<{ columns: string[]; rows: string[][] }> {
    try {
      const csv = await commercialApi.fetchGasTransitCosts(unit, opts)
      hasGasTransitPermission.value = true
      return parseCsv(csv)
    } catch (err) {
      hasGasTransitPermission.value = false
      console.warn('fetchGasTransitCosts failed', { unit, opts, err })
      return { columns: [], rows: [] }
    }
  }

  return {
    hasGasTransitPermission,
    routes,
    origins,
    destinations,
    destinationsFor,
    fetchReferenceData,
    fetchCosts,
  }
})
